import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Search, User } from "lucide-react";
import type { OrderWithCheckins } from "@/pages/producers/Scanner";

interface ManualSearchProps {
  eventId: string;
  producerId: string;
  onSelectOrder: (order: OrderWithCheckins) => void;
}

export function ManualSearch({ eventId, producerId, onSelectOrder }: ManualSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<OrderWithCheckins[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setSearched(true);

    try {
      const query = searchQuery.trim();
      console.log("Searching for:", query, "in event:", eventId);
      
      // Use secure RPC function to get orders (bypasses RLS)
      const { data: orders, error } = await supabase
        .rpc('get_producer_event_orders', {
          p_event_id: eventId,
          p_producer_id: producerId
        });

      console.log("Search query result:", { orders, error });

      if (error) throw error;
      
      // Filter for completed/paid orders and match search query
      const filteredOrders = (orders || []).filter((order: any) => {
        if (order.status !== 'completed' && order.status !== 'paid') return false;
        
        const searchLower = query.toLowerCase();
        return (
          order.user_first_name?.toLowerCase().includes(searchLower) ||
          order.user_last_name?.toLowerCase().includes(searchLower) ||
          order.user_email?.toLowerCase().includes(searchLower) ||
          order.reference?.toLowerCase().includes(searchLower)
        );
      });

      // Get check-in counts for each order
      const ordersWithCheckins: OrderWithCheckins[] = await Promise.all(
        filteredOrders.map(async (order: any) => {
          const totalTickets = (order.vip_ticket_quantity || 0) + 
                              (order.standing_tickets || 0) + 
                              (order.early_bird_tickets || 0);

          const { data: checkins } = await supabase
            .from("ticket_checkins")
            .select("checked_in_count")
            .eq("order_id", order.id);

          const checkedInCount = checkins?.reduce((sum, c) => sum + c.checked_in_count, 0) || 0;

          return {
            ...order,
            total_tickets: totalTickets,
            checked_in_count: checkedInCount,
          };
        })
      );

      setResults(ordersWithCheckins);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearch} className="flex gap-2">
        <Input
          placeholder="Search by name, email, or reference..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" disabled={loading || !searchQuery.trim()}>
          <Search className="h-4 w-4" />
        </Button>
      </form>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
        </div>
      )}

      {!loading && searched && results.length === 0 && (
        <Card className="bg-muted/50 border-border">
          <CardContent className="p-6 text-center text-muted-foreground">
            No tickets found matching "{searchQuery}"
          </CardContent>
        </Card>
      )}

      {!loading && results.length > 0 && (
        <div className="space-y-2">
          {results.map((order) => {
            const isExhausted = order.checked_in_count >= order.total_tickets;
            
            return (
              <Card 
                key={order.id}
                className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                  isExhausted ? 'border-orange-500/50' : 'border-border'
                }`}
                onClick={() => onSelectOrder(order)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${isExhausted ? 'bg-orange-500/10' : 'bg-primary/10'}`}>
                        <User className={`h-4 w-4 ${isExhausted ? 'text-orange-500' : 'text-primary'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {order.user_first_name} {order.user_last_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {order.reference} • {order.user_email}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${isExhausted ? 'text-orange-500' : 'text-foreground'}`}>
                        {order.checked_in_count}/{order.total_tickets}
                      </p>
                      <p className="text-xs text-muted-foreground">checked in</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
